import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';

/**
 * Canonical admin detail-page hero band (founder decision 2026-07-19):
 * deep-ocean banner with avatar-or-initials, the record name in Fraunces, a
 * subtitle line ("Member since Jan 2024", city/state, …) and right-aligned
 * action buttons. Generalized from the manage-clubs `.partner-header` (#1155)
 * so every entity detail page renders the SAME header instead of a per-feature
 * variant.
 *
 * All visual chrome lives in the shared design-tokens partial
 * `libs/front/shared/design-tokens/src/_admin-hero.scss` (`.adm-hero__*`,
 * imported by `overrides/_admin.scss`) — never per-component SCSS.
 *
 * Actions are projected: put routine actions (Edit toggle, …) on the
 * `hero-actions` slot and give buttons the shared `adm-hero__btn` class:
 *
 * ```html
 * <bodyanalytics-adm-detail-hero eyebrow="Customer" [title]="name" [subtitle]="memberSince" (back)="goBack()">
 *   <button hero-actions type="button" class="adm-hero__btn" (click)="onEdit()">
 *     <i class="bx bx-edit-alt" aria-hidden="true"></i> Edit
 *   </button>
 * </bodyanalytics-adm-detail-hero>
 * ```
 *
 * Destructive actions do NOT belong here — they live in the page-bottom
 * `AdmDangerZoneComponent` (R-BTN-01: Edit and Delete are never peers).
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'bodyanalytics-adm-detail-hero',
  standalone: true,
  templateUrl: './adm-detail-hero.component.html',
  styleUrls: ['./adm-detail-hero.component.scss'],
})
export class AdmDetailHeroComponent {
  /** Record name — rendered as the page `<h1>` in Fraunces. */
  readonly title = input.required<string>();
  /** Small mono uppercase kicker above the name (e.g. "Partner", "Customer"). */
  readonly eyebrow = input<string | undefined>(undefined);
  /** Secondary line under the name (e.g. "Member since Jan 2024"). */
  readonly subtitle = input<string | undefined>(undefined);
  /** Optional Boxicons glyph rendered before the subtitle (e.g. `bx-map-pin`). */
  readonly subtitleIcon = input<string | undefined>(undefined);
  /** Avatar image URL; when absent the monogram initials chip renders instead. */
  readonly avatarUrl = input<string | undefined>(undefined);
  /** Explicit monogram override; defaults to the first letters of `title`. */
  readonly initials = input<string | undefined>(undefined);
  /** Back-link label; the template falls back to its default copy when unset. */
  readonly backLabel = input<string | undefined>(undefined);
  /** Hide the back link entirely (rare — e.g. embedded previews). */
  readonly showBack = input<boolean>(true);

  /** User pressed the back link. */
  readonly back = output<void>();

  /**
   * Monogram shown when there is no avatar: the `initials` input when given,
   * otherwise the first letter of the first two words of `title` ("Jane Doe"
   * → "JD", single-word titles → first letter).
   */
  protected readonly displayInitials = computed(() => {
    const explicit = this.initials()?.trim();
    if (explicit) {
      return explicit.slice(0, 2).toUpperCase();
    }
    return this.title()
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase();
  });
}
